import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ApolloError } from "@apollo/client";
import { FlexCol, Spinner } from "@/shared/globals";
import { themeColor } from "@/shared/styles-utils";
import {
  createSearchParams,
  parseSearchParams,
} from "@/shared/utils/search-params";
import {
  useParams,
  usePathname,
  useSearchParams,
} from "@/i18n/i18n-navigation";
import { useNavigateLoading } from "@/hooks/useNavigate";

interface FilterByCategoryProps {
  availableFilters: {
    propertyName: string;
    propertyId: string;
    valueName: string;
    valueId: string;
    count?: number;
  }[];
  loading: boolean;
  error: ApolloError | undefined;
  selectedFilterIds: string[];
}

export const AvailableFilterChoices = ({
  availableFilters: availableFiltersProp,
  loading,
  error,
  selectedFilterIds,
}: FilterByCategoryProps) => {
  const [isFetching, navigate] = useNavigateLoading();
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queries = parseSearchParams(searchParams);
  const isCollectionPage = !!params.collection;

  const [availableFilters, setAvailableFilters] =
    useState(availableFiltersProp);

  useEffect(() => {
    if (availableFiltersProp?.length) {
      setAvailableFilters(availableFiltersProp);
    }
  }, [availableFiltersProp]);

  if (error && !availableFilters) {
    return null;
  }

  availableFilters?.sort((a, b) => {
    const fa = a.valueName,
      fb = b.valueName;

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  });

  return (
    <FlexCol alignItems="start">
      <StyledUL>
        <StyledLoading>
          {isFetching && <Spinner inline size={24} />}
        </StyledLoading>
        {availableFilters?.map((filter, i) => (
          <StyledLI key={i}>
            <StyledCheckBox
              type="checkbox"
              checked={
                selectedFilterIds?.includes(filter.valueId) ||
                selectedFilterIds?.includes(
                  filter?.valueId! + "|" + filter?.propertyId!
                )
              }
              id={filter?.valueId!}
              disabled={loading}
              onChange={(event) => {
                let filters = [...selectedFilterIds];
                if (event.target.checked) {
                  if (filter.valueId.startsWith("c:"))
                    filters.push(filter?.valueId!);
                  else
                    filters.push(filter?.valueId! + "|" + filter?.propertyId!);
                } else {
                  if (filter.valueId.startsWith("c:"))
                    filters = filters.filter(
                      (item) => item !== filter?.valueId!
                    );
                  else
                    filters = filters.filter(
                      (item) =>
                        item !== filter?.valueId! + "|" + filter.propertyId
                    );
                  if (isCollectionPage) {
                    navigate(`/shop`);
                    return;
                  }
                }
                if (queries.collection) {
                  filters.push(
                    availableFilters?.find(
                      (item) => item.valueName === queries.collection
                    )?.valueId!
                  );
                }

                const { query } = createSearchParams({
                  ...queries,
                  page: 1,
                  filters,
                });

                navigate(
                  isCollectionPage ? `/shop?${query}` : `${pathname}?${query}`,
                  { scroll: false }
                );
              }}
            />

            <StyledLabel htmlFor={filter.valueId!} disabled={loading}>
              {filter?.valueName}
            </StyledLabel>
          </StyledLI>
        ))}
      </StyledUL>
    </FlexCol>
  );
};

// Styles

const StyledLoading = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  z-index: -1;
  width: 90%;
`;

const StyledUL = styled.ul`
  list-style-type: none;
  padding-inline-start: 0;
  margin-bottom: 0;
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;
const StyledLI = styled.li`
  display: flex;
  align-items: flex-start;
  color: ${themeColor("secondary")};
  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
`;
const StyledLabel = styled.label<{ disabled: boolean }>`
  ${({ disabled }) => disabled && "opacity: 0.5;"}
  margin-inline-start: 4px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed;" : "pointer;")};
`;
const StyledCheckBox = styled.input<{ disabled: boolean }>`
  ${({ disabled }) => disabled && "opacity: 0.5;"};
  cursor: ${({ disabled }) => (disabled ? "not-allowed;" : "pointer;")};
  &:checked {
    accent-color: #000;
  }
`;
